import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { wayleaveDocsAPI } from '../utils/api';
import WayleaveDocDrawer from './WayleaveDocDrawer';
import { formatDate } from '../lib/date';

// The fixed six-document catalogue (spec §3). Order = submission order.
const DOC_CATALOGUE = [
  { key: 'ta_plan', name: 'TA Plan', abbr: 'TA', suppliedBy: 'Contractor' },
  { key: 'appointment_letter', name: 'Appointment Letter', abbr: 'App. Letter', suppliedBy: 'Consultant' },
  { key: 'programme', name: 'Programme', abbr: 'Prog.', suppliedBy: 'Contractor' },
  { key: 'locality_plan', name: 'Locality Plan', abbr: 'Loc. Plan', suppliedBy: 'Contractor' },
  { key: 'contact_details', name: 'Contact Details', abbr: 'Contact', suppliedBy: 'Contractor' },
  { key: 'ohs', name: 'OHS File', abbr: 'OHS', suppliedBy: 'Contractor' },
];

// Document slot status model (spec §4)
const SLOT_STATUS = {
  not_started: { label: 'Not Started', color: '#9ca3af' },
  submitted: { label: 'Awaiting Review', color: '#f59e0b' },
  in_review: { label: 'Under Review', color: '#3b82f6' },
  changes_requested: { label: 'Changes Requested', color: '#f97316' },
  accepted: { label: 'Accepted', color: '#10b981' },
  submitted_to_dre: { label: 'With DRE', color: '#6366f1' },
  dre_query: { label: 'DRE Query', color: '#ef4444' },
  approved: { label: 'Approved', color: '#059669' },
  not_applicable: { label: 'N/A', color: '#9ca3af' },
};

// Pack status model (spec §4)
const PACK_STATUS = {
  in_preparation: { label: 'In Preparation', color: '#64748b' },
  in_review: { label: 'In Review', color: '#3b82f6' },
  ready_for_dre: { label: 'Ready for DRE', color: '#8b5cf6' },
  with_dre: { label: 'With DRE', color: '#6366f1' },
  dre_query: { label: 'DRE Query', color: '#ef4444' },
  granted: { label: 'Granted', color: '#059669' },
};

const slotMeta = (status) => SLOT_STATUS[status] || SLOT_STATUS.not_started;
const packMeta = (status) => PACK_STATUS[status] || PACK_STATUS.in_preparation;

// Workflow order for sorting/filtering by pack status
const PACK_ORDER = ['in_preparation', 'in_review', 'ready_for_dre', 'with_dre', 'dre_query', 'granted'];
const packOrderIndex = (status) => {
  const i = PACK_ORDER.indexOf(status);
  return i === -1 ? 99 : i;
};
// Progress = how many of the six slots have moved past "not started" (matches the
// visual progress strip, where only not_started renders as an empty block)
const progressScore = (pack) => (pack.slots || []).filter((s) => s.status && s.status !== 'not_started').length;
// Numeric-aware station comparison (handles values like "4011b")
const compareStation = (a, b) => String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' });

function StatusDot({ color }) {
  return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />;
}

// Six-segment progress strip for the list rows — labelled blocks
function ProgressBar({ slots }) {
  return (
    <div className="grid grid-cols-6 gap-1 w-72">
      {DOC_CATALOGUE.map((doc) => {
        const slot = slots?.find((s) => s.doc_key === doc.key);
        const status = slot?.status || 'not_started';
        const isEmpty = status === 'not_started';
        return (
          <div
            key={doc.key}
            className="h-9 rounded flex items-center justify-center text-center px-0.5 leading-none"
            style={
              isEmpty
                ? { backgroundColor: '#e5e7eb', border: '1px solid #d1d5db' }
                : { backgroundColor: slotMeta(status).color }
            }
            title={`${doc.name} — ${slotMeta(status).label}`}
          >
            <span
              className="text-[9px] font-semibold leading-tight"
              style={{ color: isEmpty ? '#4b5563' : '#ffffff' }}
            >
              {doc.abbr}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card p-3">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

// The six-block status strip on the pack detail (spec §7.4).
// `onSelect(doc)` receives the whole logical document (OHS carries `parts`).
function StatusStrip({ slots, onSelect }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
      {DOC_CATALOGUE.map((doc) => {
        const slot = slots?.find((s) => s.doc_key === doc.key);
        const status = slot?.status || 'not_started';
        const meta = slotMeta(status);
        const isEmpty = status === 'not_started';
        return (
          <div key={doc.key} className="flex flex-col items-center">
            <button
              type="button"
              onClick={() => slot && onSelect && onSelect(slot)}
              className="w-full h-14 rounded-md flex items-center justify-center text-center px-1 transition-transform hover:scale-[1.03] focus:outline-none focus:ring-2 focus:ring-primary-400"
              style={
                isEmpty
                  ? { backgroundColor: '#e5e7eb', border: '1px solid #d1d5db' }
                  : { backgroundColor: meta.color }
              }
              title={`${doc.name} — ${meta.label} · click to open`}
            >
              <span
                className="text-[11px] font-bold leading-tight"
                style={{ color: isEmpty ? '#374151' : '#ffffff' }}
              >
                {doc.name}
              </span>
            </button>
            <span className="text-[10px] text-gray-500 mt-1 text-center">{meta.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// Pack activity timeline (spec §7.2), grouped by day, newest first
function Timeline({ activity, loading }) {
  if (loading) return <div className="card text-center py-10 text-gray-500">Loading timeline…</div>;
  if (!activity || activity.length === 0) return <div className="card text-center py-10 text-gray-400 text-sm">No activity yet.</div>;

  const groups = [];
  activity.forEach((a) => {
    const day = formatDate(a.created_at);
    let g = groups.find((x) => x.day === day);
    if (!g) { g = { day, items: [] }; groups.push(g); }
    g.items.push(a);
  });

  return (
    <div className="card">
      <div className="space-y-6">
        {groups.map((g) => (
          <div key={g.day}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{g.day}</h4>
            <ul className="space-y-3">
              {g.items.map((a) => (
                <li key={a.id} className="flex items-start gap-3">
                  <span className="mt-1.5 inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: slotMeta(a.status).color }} />
                  <div className="min-w-0">
                    <p className="text-sm text-gray-800">{a.detail || a.action.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-500">
                      {a.actor}{a.actor_role ? ` · ${a.actor_role}` : ''} · {new Date(a.created_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function WayleaveDocuments({ userInfo, deepLink }) {
  const isConsultant = ['admin', 'sysadmin'].includes((userInfo?.role || '').toLowerCase());

  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [queue, setQueue] = useState([]);

  const [selectedPackId, setSelectedPackId] = useState(null);
  const [packDetail, setPackDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerSlotId, setDrawerSlotId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [detailTab, setDetailTab] = useState('documents');
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [ohsExpanded, setOhsExpanded] = useState(false);
  const deepLinkHandled = useRef(null);

  // New pack modal
  const [showNewPack, setShowNewPack] = useState(false);
  const [stations, setStations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [newStation, setNewStation] = useState('');
  const [newAppointmentId, setNewAppointmentId] = useState('');
  const [newContractorId, setNewContractorId] = useState('');
  const [newConsultantId, setNewConsultantId] = useState('');
  const [newTargetDate, setNewTargetDate] = useState('');
  // Which of the five contractor documents are required (appointment letter is
  // handled by the letter picker above).
  const CONTRACTOR_DOCS = DOC_CATALOGUE.filter((d) => d.key !== 'appointment_letter');
  const [requiredDocs, setRequiredDocs] = useState(CONTRACTOR_DOCS.map((d) => d.key));
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  // Edit pack modal
  const [showEdit, setShowEdit] = useState(false);
  const [editPack, setEditPack] = useState(null);
  const [editConsultantId, setEditConsultantId] = useState('');
  const [editContractorId, setEditContractorId] = useState('');
  const [editTargetDate, setEditTargetDate] = useState('');
  const [editRequiredDocs, setEditRequiredDocs] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');

  // List-view search / filter / sort
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stationFilter, setStationFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const fetchPacks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await wayleaveDocsAPI.getPacks();
      setPacks(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document packs');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await wayleaveDocsAPI.getQueue();
      setQueue(res.data || []);
    } catch {
      setQueue([]);
    }
  }, []);

  useEffect(() => { fetchPacks(); fetchQueue(); }, [fetchPacks, fetchQueue]);

  // Load the timeline when the Timeline tab is opened
  useEffect(() => {
    if (!selectedPackId || detailTab !== 'timeline') return;
    let cancelled = false;
    setActivityLoading(true);
    wayleaveDocsAPI.getPackActivity(selectedPackId)
      .then((res) => { if (!cancelled) setActivity(res.data || []); })
      .catch(() => { if (!cancelled) setActivity([]); })
      .finally(() => { if (!cancelled) setActivityLoading(false); });
    return () => { cancelled = true; };
  }, [selectedPackId, detailTab]);

  const openPack = async (id) => {
    setSelectedPackId(id);
    setDetailTab('documents');
    setActivity([]);
    setOhsExpanded(false);
    setDetailLoading(true);
    setPackDetail(null);
    try {
      const res = await wayleaveDocsAPI.getPack(id);
      setPackDetail(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load pack');
      setSelectedPackId(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshPackDetail = async () => {
    if (!selectedPackId) return;
    try {
      const res = await wayleaveDocsAPI.getPack(selectedPackId);
      setPackDetail(res.data);
    } catch { /* keep current view */ }
  };

  const backToList = () => {
    setSelectedPackId(null);
    setPackDetail(null);
    setDrawerSlotId(null);
    fetchPacks();
    fetchQueue();
  };

  // Open a pack from a queue item and, if it references a document, its drawer
  const openPackToSlot = async (packId, slotId) => {
    await openPack(packId);
    if (slotId) setDrawerSlotId(slotId);
  };

  // Deep-link from a notification: auto-open the pack (and document). Keyed so a
  // fresh notification re-opens, but unrelated re-renders don't.
  useEffect(() => {
    if (!deepLink?.pack) return;
    const key = `${deepLink.pack}:${deepLink.slot || ''}`;
    if (deepLinkHandled.current === key) return;
    deepLinkHandled.current = key;
    openPackToSlot(Number(deepLink.pack), deepLink.slot ? Number(deepLink.slot) : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  const openNewPack = async () => {
    setShowNewPack(true);
    setNewStation('');
    setNewAppointmentId('');
    setNewContractorId('');
    setNewConsultantId('');
    setNewTargetDate('');
    setRequiredDocs(CONTRACTOR_DOCS.map((d) => d.key));
    setCreateError('');
    try {
      const [st, ap, co, cons] = await Promise.all([
        stations.length === 0 ? wayleaveDocsAPI.getWayleaveStations() : Promise.resolve({ data: stations }),
        wayleaveDocsAPI.getAppointments(),
        wayleaveDocsAPI.getContractors(),
        wayleaveDocsAPI.getConsultants(),
      ]);
      setStations(st.data || []);
      setAppointments(ap.data || []);
      setContractors(co.data || []);
      setConsultants(cons.data || []);
    } catch {
      // Non-fatal — the station picker may still work; the rest are optional
    }
  };

  const openEdit = async (pack) => {
    setEditPack(pack);
    setEditConsultantId(pack.consultant_user_id || '');
    setEditContractorId(pack.contractor_user_id || '');
    setEditTargetDate(pack.target_date ? new Date(pack.target_date).toISOString().slice(0, 10) : '');
    setEditRequiredDocs(
      CONTRACTOR_DOCS.map((d) => d.key).filter((key) => {
        const slot = (pack.slots || []).find((s) => s.doc_key === key);
        return !slot || slot.status !== 'not_applicable';
      })
    );
    setEditError('');
    setShowEdit(true);
    try {
      const [co, cons] = await Promise.all([
        contractors.length ? Promise.resolve({ data: contractors }) : wayleaveDocsAPI.getContractors(),
        consultants.length ? Promise.resolve({ data: consultants }) : wayleaveDocsAPI.getConsultants(),
      ]);
      setContractors(co.data || []);
      setConsultants(cons.data || []);
    } catch { /* optional */ }
  };

  const toggleEditDoc = (key) => {
    setEditRequiredDocs((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const saveEdit = async () => {
    if (!editPack) return;
    setSavingEdit(true);
    setEditError('');
    try {
      await wayleaveDocsAPI.updatePack(editPack.id, {
        consultantUserId: editConsultantId || null,
        contractorUserId: editContractorId || null,
        targetDate: editTargetDate || null,
        requiredDocs: editRequiredDocs,
      });
      setShowEdit(false);
      await fetchPacks();
      if (selectedPackId === editPack.id) await refreshPackDetail();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update pack');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleRequiredDoc = (key) => {
    setRequiredDocs((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const createPack = async () => {
    if (!newStation) { setCreateError('Please select a station'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const res = await wayleaveDocsAPI.createPack({
        stationNo: newStation,
        appointmentId: newAppointmentId || null,
        contractorUserId: newContractorId || null,
        consultantUserId: newConsultantId || null,
        targetDate: newTargetDate || null,
        // appointment letter is always part of the pack; its status follows the picker
        requiredDocs: [...requiredDocs, 'appointment_letter'],
      });
      setShowNewPack(false);
      await fetchPacks();
      if (res.data?.id) openPack(res.data.id);
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create pack');
    } finally {
      setCreating(false);
    }
  };

  const submitToDre = async () => {
    if (!packDetail) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await wayleaveDocsAPI.submitPack(packDetail.id);
      setShowSubmitConfirm(false);
      await refreshPackDetail();
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Failed to submit pack');
    } finally {
      setSubmitting(false);
    }
  };

  // Clicking a status block: OHS expands its parts; others open the drawer
  const handleDocSelect = (doc) => {
    if (doc.isBundle) setOhsExpanded(true);
    else if (doc.id) setDrawerSlotId(doc.id);
  };

  const toggleAddendum = async (part, applicable) => {
    try {
      await wayleaveDocsAPI.setSlotNotApplicable(part.id, applicable);
      await refreshPackDetail();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update the addendum');
    }
  };

  const contractorName = (c) =>
    (c.firstName || c.lastName) ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : c.username;

  const deletePack = async (id, stationNo, fromDetail) => {
    if (!window.confirm(`Delete the document pack for station ${stationNo}? This removes all its documents and cannot be undone.`)) {
      return;
    }
    try {
      await wayleaveDocsAPI.deletePack(id);
      if (fromDetail) {
        setSelectedPackId(null);
        setPackDetail(null);
      }
      await fetchPacks();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete pack');
    }
  };

  // Stations already having a pack (to disable in the picker)
  const stationsWithPacks = new Set(packs.map((p) => String(p.station_no)));

  // ---- Stat card counts ----
  const counts = packs.reduce((acc, p) => {
    acc[p.derivedStatus] = (acc[p.derivedStatus] || 0) + 1;
    return acc;
  }, {});

  // ---- Search / filter / sort for the list view ----
  // Distinct stations present in the packs (for the Station filter dropdown)
  const stationOptions = useMemo(() => {
    const seen = new Map();
    packs.forEach((p) => {
      const key = String(p.station_no);
      if (!seen.has(key)) seen.set(key, (p.description || '').trim());
    });
    return Array.from(seen, ([no, desc]) => ({ no, desc })).sort((a, b) => compareStation(a.no, b.no));
  }, [packs]);

  // Statuses actually present (for the Status filter dropdown), in workflow order
  const statusOptions = useMemo(() => {
    const present = new Set(packs.map((p) => p.derivedStatus));
    return PACK_ORDER.filter((s) => present.has(s));
  }, [packs]);

  const visiblePacks = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = packs.filter((p) => {
      if (q) {
        const hay = `${p.station_no} ${p.description || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter && p.derivedStatus !== statusFilter) return false;
      if (stationFilter && String(p.station_no) !== stationFilter) return false;
      if (createdFrom && new Date(p.created_at) < new Date(`${createdFrom}T00:00:00`)) return false;
      if (createdTo && new Date(p.created_at) > new Date(`${createdTo}T23:59:59.999`)) return false;
      return true;
    });
    if (sortConfig.key) {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;
      const cmp = (a, b) => {
        switch (sortConfig.key) {
          case 'station_no': return compareStation(a.station_no, b.station_no);
          case 'owner': return String(a.owner || '').localeCompare(String(b.owner || ''), undefined, { sensitivity: 'base' });
          case 'status': return packOrderIndex(a.derivedStatus) - packOrderIndex(b.derivedStatus);
          case 'progress': return progressScore(a) - progressScore(b);
          case 'accepted': return (a.acceptedCount || 0) - (b.acceptedCount || 0);
          case 'created_at': return new Date(a.created_at) - new Date(b.created_at);
          default: return 0;
        }
      };
      list = [...list].sort((a, b) => dir * cmp(a, b));
    }
    return list;
  }, [packs, search, statusFilter, stationFilter, createdFrom, createdTo, sortConfig]);

  const anyFilterActive = !!(search || statusFilter || stationFilter || createdFrom || createdTo);
  const resetFilters = () => {
    setSearch(''); setStatusFilter(''); setStationFilter(''); setCreatedFrom(''); setCreatedTo('');
  };
  const toggleSort = (key) => setSortConfig((prev) => (
    prev.key === key
      ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
      : { key, direction: 'asc' }
  ));

  const sortTh = (key, label) => {
    const active = sortConfig.key === key;
    return (
      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <button
          type="button"
          onClick={() => toggleSort(key)}
          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700 focus:outline-none"
        >
          {label}
          <span className={active ? 'text-primary-600' : 'text-gray-300'}>
            {active ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  const renderControls = () => (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by station number or name…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <span className="text-xs text-gray-500 whitespace-nowrap">Showing {visiblePacks.length} of {packs.length}</span>
        {anyFilterActive && (
          <button onClick={resetFilters} className="text-xs font-medium text-primary-600 hover:text-primary-800 px-2 py-1">
            Reset filters
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[150px]">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All statuses</option>
            {statusOptions.map((s) => <option key={s} value={s}>{packMeta(s).label}</option>)}
          </select>
        </div>
        <div className="min-w-[180px]">
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Station</label>
          <select
            value={stationFilter}
            onChange={(e) => setStationFilter(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All stations</option>
            {stationOptions.map((s) => (
              <option key={s.no} value={s.no}>{s.no}{s.desc ? ` — ${s.desc}` : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Created from</label>
          <input
            type="date"
            value={createdFrom}
            onChange={(e) => setCreatedFrom(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Created to</label>
          <input
            type="date"
            value={createdTo}
            onChange={(e) => setCreatedTo(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  const renderEditModal = () => {
    if (!showEdit || !editPack) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => !savingEdit && setShowEdit(false)}>
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Edit pack — Station {editPack.station_no}</h3>
          <p className="text-sm text-gray-500 mb-4">Reassign the pack or change which documents are required.</p>

          <div className="space-y-4">
            {/* Consultant owner */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Assign consultant</label>
              <select
                value={editConsultantId}
                onChange={(e) => setEditConsultantId(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Unassigned</option>
                {consultants.map((c) => (
                  <option key={c.id} value={c.id}>{contractorName(c)}</option>
                ))}
              </select>
            </div>

            {/* Contractor + target date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assign contractor</label>
                <select
                  value={editContractorId}
                  onChange={(e) => setEditContractorId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Unassigned</option>
                  {contractors.map((c) => (
                    <option key={c.id} value={c.id}>{contractorName(c)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Target date</label>
                <input
                  type="date"
                  value={editTargetDate}
                  onChange={(e) => setEditTargetDate(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Required documents */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">Which documents are required?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {CONTRACTOR_DOCS.map((d) => (
                  <label key={d.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editRequiredDocs.includes(d.key)}
                      onChange={() => toggleEditDoc(d.key)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    {d.name}
                  </label>
                ))}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">Unticking a document marks it N/A. Documents already under review are left unchanged.</p>
            </div>
          </div>

          {editError && <p className="text-xs text-red-600 mt-3">{editError}</p>}

          <div className="flex justify-end gap-2 mt-5">
            <button
              onClick={() => setShowEdit(false)}
              disabled={savingEdit}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={saveEdit}
              disabled={savingEdit}
              className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {savingEdit ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ===================== DETAIL VIEW =====================
  if (selectedPackId) {
    return (
      <div className="w-full">
        <button
          onClick={backToList}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 mb-3"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to packs
        </button>

        {detailLoading ? (
          <div className="card text-center py-12 text-gray-500">Loading pack…</div>
        ) : !packDetail ? (
          <div className="card text-center py-12 text-gray-500">Pack not found.</div>
        ) : (
          <>
            {/* Pack header */}
            <div className="card mb-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-gray-900">Station {packDetail.station_no}</h2>
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ backgroundColor: `${packMeta(packDetail.derivedStatus).color}1a`, color: packMeta(packDetail.derivedStatus).color }}
                    >
                      <StatusDot color={packMeta(packDetail.derivedStatus).color} />
                      {packMeta(packDetail.derivedStatus).label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {[packDetail.owner, packDetail.route, packDetail.description].filter(Boolean).join(' · ') || '—'}
                  </p>
                  {packDetail.appointment && (
                    <p className="text-xs text-gray-600 mt-1">
                      Appointment letter: <span className="font-medium">{packDetail.appointment.task_order_ref}</span>
                      {packDetail.appointment.fileUrl && (
                        <a href={packDetail.appointment.fileUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-800 ml-2">View</a>
                      )}
                    </p>
                  )}
                  {(packDetail.consultantName || packDetail.contractorName) && (
                    <p className="text-xs text-gray-600 mt-1">
                      {packDetail.consultantName && <>Consultant: <span className="font-medium">{packDetail.consultantName}</span></>}
                      {packDetail.consultantName && packDetail.contractorName && <span className="mx-2 text-gray-300">|</span>}
                      {packDetail.contractorName && <>Contractor: <span className="font-medium">{packDetail.contractorName}</span></>}
                    </p>
                  )}
                </div>
                {isConsultant && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(() => {
                      const ready = packDetail.derivedStatus === 'ready_for_dre';
                      const sub = packDetail.submission;
                      const pending = sub && !sub.outcome;
                      let title = 'All required documents must be accepted before submitting';
                      if (pending) title = 'Awaiting the DRE decision';
                      else if (packDetail.derivedStatus === 'granted') title = 'Wayleave already granted';
                      else if (ready) title = 'Submit the accepted documents to the DRE';
                      return (
                        <button
                          onClick={() => { setSubmitError(''); setShowSubmitConfirm(true); }}
                          disabled={!ready || submitting}
                          className="px-3 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
                          title={title}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {submitting ? 'Submitting…' : (sub ? 'Re-submit to DRE' : 'Submit to DRE')}
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => openEdit(packDetail)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                      title="Edit this pack"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => deletePack(packDetail.id, packDetail.station_no, true)}
                      className="px-3 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-1.5"
                      title="Delete this pack and start again"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete pack
                    </button>
                  </div>
                )}
              </div>

              {submitError && <p className="text-xs text-red-600 mt-2">{submitError}</p>}

              {/* Six-block status strip */}
              <div className="mt-4">
                <StatusStrip slots={packDetail.slots} onSelect={handleDocSelect} />
              </div>
            </div>

            {/* Detail tabs */}
            <div className="border-b border-gray-200 mb-4">
              <nav className="flex gap-1">
                {[['documents', 'Documents'], ['timeline', 'Timeline'], ['dre', 'DRE Submissions']].map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setDetailTab(key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                      detailTab === key
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            {detailTab === 'documents' && (
            <div className="card">
              <h3 className="font-semibold text-gray-900 mb-3 text-base">Documents</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplied by</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revision</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last updated</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {packDetail.slots.map((slot) => {
                      const meta = slotMeta(slot.status);
                      const suppliedBy = DOC_CATALOGUE.find((d) => d.key === slot.doc_key)?.suppliedBy || '—';
                      const fmt = (d) => formatDate(d);
                      const canToggleAddendum = isConsultant || (userInfo?.role || '').toLowerCase() === 'contractor';

                      // OHS bundle: parent row (expandable) + three part sub-rows
                      if (slot.isBundle) {
                        return (
                          <React.Fragment key={slot.doc_key}>
                            <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setOhsExpanded((v) => !v)}>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                <span className="inline-flex items-center gap-1.5">
                                  <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${ohsExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                  {slot.name}
                                  <span className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">bundle</span>
                                  <span className="relative inline-flex group/ohs" onClick={(e) => e.stopPropagation()}>
                                    <button
                                      type="button"
                                      aria-label="What's in the OHS File"
                                      className="text-gray-400 hover:text-primary-600 focus:text-primary-600 focus:outline-none"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                      </svg>
                                    </button>
                                    <div className="absolute left-0 bottom-full mb-1.5 z-40 w-64 hidden group-hover/ohs:block group-focus-within/ohs:block rounded-lg border border-gray-200 bg-white p-3 text-left shadow-lg cursor-default font-normal normal-case tracking-normal">
                                      <p className="text-xs font-semibold text-gray-900 mb-1.5">This bundle needs 3 documents:</p>
                                      <ul className="text-xs text-gray-600 space-y-0.5">
                                        <li>• HIRA</li>
                                        <li>• Health &amp; Safety Plan</li>
                                        <li>• Addendum to the H&amp;S Plan <span className="text-gray-400">(optional)</span></li>
                                      </ul>
                                      <p className="text-[11px] text-gray-400 mt-2">Click the row to expand and upload each.</p>
                                    </div>
                                  </span>
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{suppliedBy}</td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
                                  <StatusDot color={meta.color} />
                                  {meta.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-400">—</td>
                              <td className="px-4 py-3 text-xs text-gray-400">{ohsExpanded ? 'Hide parts' : 'Click to open 3 documents'}</td>
                            </tr>
                            {ohsExpanded && (slot.parts || []).map((part) => {
                              const pMeta = slotMeta(part.status);
                              const isAddendum = part.part_key === 'hs_addendum';
                              const isNA = part.status === 'not_applicable';
                              return (
                                <tr key={part.id} className="bg-gray-50/60 hover:bg-gray-100 cursor-pointer" onClick={() => setDrawerSlotId(part.id)}>
                                  <td className="px-4 py-2.5 text-sm text-gray-700 pl-10">
                                    <span className="text-gray-400 mr-1">↳</span>{part.name}
                                    {isAddendum && <span className="text-[10px] text-gray-400 ml-1">(optional)</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-sm text-gray-500">Contractor</td>
                                  <td className="px-4 py-2.5">
                                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: `${pMeta.color}1a`, color: pMeta.color }}>
                                      <StatusDot color={pMeta.color} />
                                      {pMeta.label}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-sm text-gray-600">{part.revision_no != null ? `R${part.revision_no}` : '—'}</td>
                                  <td className="px-4 py-2.5 text-xs text-gray-500">
                                    {isAddendum && canToggleAddendum ? (
                                      <button
                                        onClick={(e) => { e.stopPropagation(); toggleAddendum(part, isNA); }}
                                        className="text-primary-600 hover:text-primary-800 font-medium"
                                      >
                                        {isNA ? 'Mark required' : 'Mark N/A'}
                                      </button>
                                    ) : fmt(part.updated_at)}
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      }

                      // Regular single-slot document
                      return (
                        <tr key={slot.id || slot.doc_key} className="hover:bg-gray-50 cursor-pointer" onClick={() => slot.id && setDrawerSlotId(slot.id)}>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{slot.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{suppliedBy}</td>
                          <td className="px-4 py-3">
                            <span
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
                            >
                              <StatusDot color={meta.color} />
                              {meta.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {slot.revision_no != null ? `R${slot.revision_no}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {fmt(slot.updated_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Click a document row or a status block above to review it, request changes, or accept.
              </p>
            </div>
            )}

            {detailTab === 'timeline' && (
              <Timeline activity={activity} loading={activityLoading} />
            )}

            {/* DRE Submissions — track the submission to the DRE and their response */}
            {detailTab === 'dre' && (() => {
              const sub = packDetail.submission;
              const status = packDetail.derivedStatus;
              const outcomeColor = sub?.outcome === 'approved' ? '#059669'
                : sub?.outcome === 'declined' ? '#ef4444'
                : sub?.outcome === 'query' ? '#f59e0b' : '#6366f1';
              const fmtD = (d) => formatDate(d);
              return (
                <div className="card mt-4">
                  <h3 className="font-semibold text-gray-900 mb-1 text-base">DRE Submissions</h3>
                  <p className="text-sm text-gray-500 mb-3">
                    Tracks the submission to the DRE and their response. Use <span className="font-medium">Submit to DRE</span> at the top of the pack once every required document is accepted.
                  </p>

                  {!sub ? (
                    <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center">
                      <p className="text-sm text-gray-500">
                        {status === 'ready_for_dre'
                          ? 'Ready to submit — use “Submit to DRE” at the top of the pack.'
                          : 'Not yet submitted. All required documents must be accepted first.'}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="text-sm text-gray-800 font-medium">Submitted to {sub.submitted_to || 'DRE'}</p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {fmtD(sub.submitted_at)} by {sub.submitted_by || '—'}{sub.dre_ref ? ` · Ref ${sub.dre_ref}` : ''}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium capitalize" style={{ backgroundColor: `${outcomeColor}1a`, color: outcomeColor }}>
                          <StatusDot color={outcomeColor} />
                          {sub.outcome ? `DRE ${sub.outcome}` : 'Awaiting decision'}
                        </span>
                      </div>

                      {sub.outcome && (
                        <div className="mt-3 pt-3 border-t border-gray-100 text-sm">
                          {sub.outcome === 'approved' && (sub.valid_from || sub.valid_to) && (
                            <p className="text-gray-700">Valid {fmtD(sub.valid_from)}{sub.valid_to ? ` – ${fmtD(sub.valid_to)}` : ''}</p>
                          )}
                          {sub.outcome_reason && (
                            <p className="text-gray-700 mt-1"><span className="text-gray-400">Reason:</span> {sub.outcome_reason}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">Decided {fmtD(sub.decided_at)}{sub.decided_by ? ` by ${sub.decided_by}` : ''}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </>
        )}

        {drawerSlotId && (
          <WayleaveDocDrawer
            slotId={drawerSlotId}
            userInfo={userInfo}
            onClose={() => setDrawerSlotId(null)}
            onChanged={refreshPackDetail}
          />
        )}
        {renderEditModal()}

        {/* Submit-to-DRE confirmation (in-app) */}
        {showSubmitConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => !submitting && setShowSubmitConfirm(false)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Submit to DRE?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Submit the pack for <span className="font-medium">Station {packDetail?.station_no}</span> to the DRE. This locks the accepted documents into a submission at their current revisions.
              </p>
              {submitError && <p className="text-xs text-red-600 mb-3">{submitError}</p>}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowSubmitConfirm(false)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={submitToDre}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Submit to DRE'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===================== LIST VIEW =====================
  // Reusable pieces so the contractor (two-column) and consultant layouts share markup
  const queueList = (
    <div className="divide-y divide-gray-100">
      {queue.map((item, i) => (
        <button
          key={`${item.pack_id}-${item.slot_id ?? 'pack'}-${i}`}
          onClick={() => openPackToSlot(item.pack_id, item.slot_id)}
          className="w-full text-left py-2.5 px-2 -mx-2 flex items-center justify-between gap-3 hover:bg-gray-50 rounded"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              Station {item.station_no}{item.station_name ? ` — ${item.station_name}` : ''}{item.doc_name ? ` · ${item.doc_name}` : ''}
            </p>
            <p className="text-xs text-gray-500">{item.reason}</p>
          </div>
          {item.ageDays != null && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                item.ageDays >= 5 ? 'bg-red-100 text-red-700'
                  : item.ageDays >= 3 ? 'bg-amber-100 text-amber-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {item.ageDays === 0 ? 'today' : `${item.ageDays}d`}
            </span>
          )}
        </button>
      ))}
    </div>
  );

  const myActionsHeader = (
    <h3 className="font-semibold text-gray-900 mb-2 text-base flex items-center gap-2">
      My Actions
      <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-primary-600 rounded-full">
        {queue.length}
      </span>
    </h3>
  );

  const statCards = (
    <>
      <StatCard label="Total packs" value={packs.length} color="#0f172a" />
      <StatCard label="In Preparation" value={counts.in_preparation || 0} color={PACK_STATUS.in_preparation.color} />
      <StatCard label="In Review" value={counts.in_review || 0} color={PACK_STATUS.in_review.color} />
      <StatCard label="Ready for DRE" value={counts.ready_for_dre || 0} color={PACK_STATUS.ready_for_dre.color} />
      <StatCard label="With DRE" value={counts.with_dre || 0} color={PACK_STATUS.with_dre.color} />
    </>
  );

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Wayleave Documents</h2>
          <p className="text-sm text-gray-600">Document packs collected and submitted to the DRE, per station.</p>
        </div>
        {isConsultant && (
          <button
            onClick={openNewPack}
            className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New pack
          </button>
        )}
      </div>

      {/* Two-column top: My Actions (col 1) beside the stats (col 2), then the table below */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 items-start">
        <div className="card">
          {myActionsHeader}
          {queue.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">Nothing waiting on you.</p>
          ) : (
            queueList
          )}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {statCards}
        </div>
      </div>

      {/* Packs table */}
      <div className="card">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading packs…</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
        ) : packs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-gray-500">No document packs yet.</p>
            {isConsultant && (
              <button onClick={openNewPack} className="mt-3 text-sm text-primary-600 hover:text-primary-800 font-medium">
                Create the first pack
              </button>
            )}
          </div>
        ) : (
          <>
            {renderControls()}
            {visiblePacks.length === 0 ? (
              <div className="text-center py-10 text-sm text-gray-500">No packs match your filters.</div>
            ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {sortTh('station_no', 'Station')}
                  {sortTh('owner', 'Owner')}
                  {sortTh('status', 'Status')}
                  {sortTh('progress', 'Progress')}
                  {sortTh('accepted', 'Accepted')}
                  {sortTh('created_at', 'Created')}
                  {isConsultant && <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visiblePacks.map((pack) => {
                  const meta = packMeta(pack.derivedStatus);
                  return (
                    <tr key={pack.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openPack(pack.id)}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{pack.station_no}</div>
                        {pack.description && pack.description.trim() && (
                          <div className="text-xs text-gray-500 truncate max-w-[180px]">{pack.description.trim()}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{pack.owner || '—'}</td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}
                        >
                          <StatusDot color={meta.color} />
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3"><ProgressBar slots={pack.slots} /></td>
                      <td className="px-4 py-3 text-sm text-gray-600">{pack.acceptedCount}/{pack.totalCount}</td>
                      <td className="px-4 py-3 text-xs text-gray-500">
                        {formatDate(pack.created_at)}
                      </td>
                      {isConsultant && (
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            onClick={(e) => { e.stopPropagation(); openEdit(pack); }}
                            className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors mr-1"
                            title="Edit pack"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); deletePack(pack.id, pack.station_no, false); }}
                            className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors"
                            title="Delete pack"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
            )}
          </>
        )}
      </div>

      {/* New pack modal */}
      {showNewPack && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={() => !creating && setShowNewPack(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">New document pack</h3>
            <p className="text-sm text-gray-500 mb-4">Set up the pack for a station. Six document slots are created automatically.</p>

            <div className="space-y-4">
              {/* Station */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Station <span className="text-red-500">*</span></label>
                <select
                  value={newStation}
                  onChange={(e) => setNewStation(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select a station…</option>
                  {stations.map((s) => {
                    const has = stationsWithPacks.has(String(s.StationNo));
                    return (
                      <option key={s.StationNo} value={s.StationNo} disabled={has}>
                        {s.StationNo}{s.Description ? ` — ${s.Description}` : ''}{has ? ' (has pack)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              {/* Appointment letter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Which appointment letter do you want to attach?</label>
                <select
                  value={newAppointmentId}
                  onChange={(e) => setNewAppointmentId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">None yet — link later</option>
                  {appointments.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.task_order_ref}{a.title ? ` — ${a.title}` : ''}{a.issued_by ? ` (${a.issued_by})` : ''}
                    </option>
                  ))}
                </select>
                {appointments.length === 0 && (
                  <p className="text-[11px] text-gray-400 mt-1">No appointment letters yet — add them under the Setup tab.</p>
                )}
                {newAppointmentId && (
                  <p className="text-[11px] text-emerald-600 mt-1">The Appointment Letter slot will be marked Accepted.</p>
                )}
              </div>

              {/* Consultant owner */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Assign consultant <span className="text-gray-400">(optional)</span></label>
                <select
                  value={newConsultantId}
                  onChange={(e) => setNewConsultantId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Unassigned</option>
                  {consultants.map((c) => (
                    <option key={c.id} value={c.id}>{contractorName(c)}</option>
                  ))}
                </select>
              </div>

              {/* Contractor + target date */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Assign contractor <span className="text-gray-400">(optional)</span></label>
                  <select
                    value={newContractorId}
                    onChange={(e) => setNewContractorId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Unassigned</option>
                    {contractors.map((c) => (
                      <option key={c.id} value={c.id}>{contractorName(c)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Target date <span className="text-gray-400">(optional)</span></label>
                  <input
                    type="date"
                    value={newTargetDate}
                    onChange={(e) => setNewTargetDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Required documents */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">Which documents are required?</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {CONTRACTOR_DOCS.map((d) => (
                    <label key={d.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requiredDocs.includes(d.key)}
                        onChange={() => toggleRequiredDoc(d.key)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {d.name}
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">Unticked documents are marked N/A on the pack.</p>
              </div>
            </div>

            {createError && <p className="text-xs text-red-600 mt-3">{createError}</p>}

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowNewPack(false)}
                disabled={creating}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={createPack}
                disabled={creating || !newStation}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create pack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {renderEditModal()}
    </div>
  );
}

export default WayleaveDocuments;
